import { useEffect, useState } from "react";
import { getUnmatchedMessages, dismissMessage } from "../../../services/crmMessages";
import type { CrmMessageResponse } from "../../../types/crm";
import {
  MAX_RESTORED_PAGES,
  loadCrmViewState,
  readPageParam,
  saveCrmViewState,
  unmatchedQueueScrollKey,
  unmatchedQueueViewKey,
} from "../utils/crmViewState";
import { useCrmScrollRestoration } from "../hooks/useCrmScrollRestoration";
import "./UnmatchedQueuePage.css";

const readStoredPage = (): number =>
  Math.min(
    readPageParam(loadCrmViewState(unmatchedQueueViewKey())?.get("page") ?? null),
    MAX_RESTORED_PAGES
  );

export const UnmatchedQueuePage = () => {
  const [messages, setMessages] = useState<CrmMessageResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [isRestored, setIsRestored] = useState(false);

  const loadMessages = async (pageNumber: number) => {
    setLoading(true);
    setError("");
    try {
      const data = await getUnmatchedMessages(pageNumber);
      if (pageNumber === 1) {
        setMessages(data.items);
      } else {
        setMessages((prev) => [...prev, ...data.items]);
      }
      setTotalCount(data.totalCount);
      setPage(pageNumber);
    } catch {
      setError("Failed to load unmatched messages.");
    } finally {
      setLoading(false);
    }
  };

  // Re-pull however far the user had loaded before navigating away, so "Load more"
  // progress isn't lost. Runs once on mount.
  useEffect(() => {
    // Read the depth before anything else — the persist effect below runs on this same
    // mount with page still 1, which clears the stored value.
    const target = readStoredPage();
    const restoreThroughStoredPage = async () => {
      for (let pageNumber = 1; pageNumber <= target; pageNumber++) {
        await loadMessages(pageNumber);
      }
      setIsRestored(true);
    };
    restoreThroughStoredPage();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", String(page));
    saveCrmViewState(unmatchedQueueViewKey(), params);
  }, [page]);

  // Wait for the restore loop to finish — restoring the offset against a partially
  // loaded list would clamp it to the shorter content.
  useCrmScrollRestoration(
    unmatchedQueueScrollKey(),
    isRestored && !loading && messages.length > 0
  );

  const handleDismiss = async (id: string) => {
    setDismissing(id);
    try {
      await dismissMessage(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setTotalCount((c) => c - 1);
    } catch {
      setError("Failed to dismiss message.");
    } finally {
      setDismissing(null);
    }
  };

  return (
    <div className="unmatched-queue">
      <div className="unmatched-queue__header">
        <h2 className="unmatched-queue__title">Unmatched Messages</h2>
        <p className="unmatched-queue__subtitle">
          Messages that could not be automatically matched to a contact.
          {totalCount > 0 && ` ${totalCount} pending.`}
        </p>
      </div>

      {error && <div className="unmatched-queue__error">{error}</div>}

      {loading && page === 1 ? (
        <div className="unmatched-queue__loading">Loading...</div>
      ) : messages.length === 0 ? (
        <div className="unmatched-queue__empty">No unmatched messages.</div>
      ) : (
        <>
          <div className="unmatched-queue__list">
            {messages.map((msg) => (
              <div key={msg.id} className="unmatched-queue__item">
                <div className="unmatched-queue__meta">
                  <span className="unmatched-queue__channel">{msg.channel}</span>
                  <span className="unmatched-queue__time">
                    {new Date(msg.occurredAt).toLocaleString()}
                  </span>
                </div>
                {msg.senderIdentifier && (
                  <div className="unmatched-queue__sender">
                    From: {msg.senderIdentifier}
                  </div>
                )}
                {msg.subject && (
                  <div className="unmatched-queue__subject">{msg.subject}</div>
                )}
                {msg.body && (
                  <div className="unmatched-queue__body">{msg.body}</div>
                )}
                <div className="unmatched-queue__actions">
                  <button
                    className="unmatched-queue__btn unmatched-queue__btn--dismiss"
                    onClick={() => handleDismiss(msg.id)}
                    disabled={dismissing === msg.id}
                  >
                    {dismissing === msg.id ? "Dismissing..." : "Dismiss"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {messages.length < totalCount && (
            <button
              className="unmatched-queue__load-more"
              onClick={() => loadMessages(page + 1)}
              disabled={loading}
            >
              {loading ? "Loading..." : `Load more (${totalCount - messages.length} remaining)`}
            </button>
          )}
        </>
      )}
    </div>
  );
};
